import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { can } from "src/Common/function";
import UserService from "src/Services/API/user.service";
import WOService from "src/Services/API/wo.service";

const REFRESH_MS = 60 * 1000;

/**
 * Number of customer requests waiting for approval (status 0, the rows the
 * Work Orders list highlights pink), for the red badge on the side menu's
 * Work Orders / Customer Requests items.
 *
 * Uses the work order list endpoint with a one-row page and reads its
 * totalCount, so it is scoped to the branches the user can see, same as the
 * list. Re-checked on every page change (e.g. after approving one), every
 * minute, and when the browser tab regains focus.
 */
function usePendingRequestCount() {
  const location = useLocation();
  const [count, setCount] = useState(0);
  const allowed = can("wo:read");

  const refresh = useCallback(() => {
    const user = UserService.getCurrentUser();
    if (!allowed || !user) return;
    WOService.getAllWorkOrders(user.id, "", 0, { status: 0 }, null, 0, 1, 0)
      .then((response) => setCount(Number(response.data?.totalCount) || 0))
      .catch(() => {});
  }, [allowed]);

  useEffect(() => {
    refresh();
  }, [refresh, location.pathname, location.search]);

  useEffect(() => {
    const timer = setInterval(refresh, REFRESH_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refresh]);

  return count;
}

export default usePendingRequestCount;
